import React, { useState, useEffect } from 'react'
import {
  Breadcrumb,
  Button,
  Card,
  Container,
  Dropdown,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Table,
} from 'react-bootstrap'
import { useHistory, useLocation } from 'react-router'
import useCreateUser from 'hooks/use-create-user'
import { useFetchTeams } from 'hooks/use-fetch-teams'
import useOrganizationsSearch from 'hooks/use-organizations-search'
import useCreateBulkUpload from 'hooks/use-create-bulk-upload'
import useDebounce from 'hooks/use-debounce'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { Organization } from 'interfaces/organization'
import toast from 'react-hot-toast'

type BulkRow = {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  work_phone: string
  location: string
  title: string
  company: string
  role: string
  team_name: string
  can_edit_tiles: string
  form_field_notes: string
  page_template_id: string
  handle: string
  system_message: string
}

const CSV_HEADER_MAP: Record<string, keyof BulkRow> = {
  'first name*': 'first_name',
  'first name': 'first_name',
  'last name': 'last_name',
  'email*': 'email',
  email: 'email',
  'mobile phone': 'phone_number',
  'work phone': 'work_phone',
  location: 'location',
  'job title': 'title',
  'company name': 'company',
  'designation (team admin/team member)': 'role',
  'designation (team admin/team member)*': 'role',
  designation: 'role',
  'team name*': 'team_name',
  'team name': 'team_name',
  'can edit page content (default: true)': 'can_edit_tiles',
  'can edit page content': 'can_edit_tiles',
  'contact card notes': 'form_field_notes',
  'template id (for internal use)': 'page_template_id',
  'template id': 'page_template_id',
  handle: 'handle',
  'handle override (use carefully)': 'handle',
}

const parseCSV = (text: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += c
      }
    } else if (c === '"') {
      inQuotes = true
    } else if (c === ',') {
      row.push(cur)
      cur = ''
    } else if (c === '\r') {
      // swallow; \n handles row break
    } else if (c === '\n') {
      row.push(cur)
      cur = ''
      if (row.some((v) => v !== '')) rows.push(row)
      row = []
    } else {
      cur += c
    }
  }
  if (cur !== '' || row.length) {
    row.push(cur)
    if (row.some((v) => v !== '')) rows.push(row)
  }
  return rows
}

const emptyBulkRow = (): BulkRow => ({
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  work_phone: '',
  location: '',
  title: '',
  company: '',
  role: '',
  team_name: '',
  can_edit_tiles: '',
  form_field_notes: '',
  page_template_id: '',
  handle: '',
  system_message: '',
})

const validateBulkRow = (row: BulkRow): string[] => {
  const errors: string[] = []
  if (!row.first_name) errors.push('first_name is required')
  if (!row.email) errors.push('email is required')
  if (!row.role) errors.push('designation is required')
  if (!row.team_name) errors.push('team_name is required')
  if (row.email && !/^.+@.+\..+$/.test(row.email))
    errors.push('email must be valid')
  if (row.role && !/^Team (?:Member|Admin)$/.test(row.role))
    errors.push('designation must be "Team Member" or "Team Admin"')
  if (row.page_template_id && !/^\d+$/.test(row.page_template_id))
    errors.push('page_template_id must be an integer')
  if (row.handle && !/^[A-Za-z\d][\w\d]{2,31}$/.test(row.handle))
    errors.push('handle format invalid')
  return errors
}

const UserCreatePage = () => {
  const history = useHistory()
  const location = useLocation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [orgId, setOrgId] = useState<string | null>(null)
  const [orgName, setOrgName] = useState<string | null>(null)
  const [orgIdFromUrl, setOrgIdFromUrl] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)

  // Org picker state
  const [orgQuery, setOrgQuery] = useState('')
  const debouncedOrgQuery = useDebounce(orgQuery, 400)
  const { data: orgSearchData, isFetching: isSearchingOrgs } =
    useOrganizationsSearch({ query: debouncedOrgQuery })
  const orgResults: Organization[] = orgSearchData?.organizations || []

  // Bulk upload state
  const [csvFileName, setCsvFileName] = useState<string | null>(null)
  const [bulkValid, setBulkValid] = useState<BulkRow[]>([])
  const [bulkInvalid, setBulkInvalid] = useState<BulkRow[]>([])
  const [bulkParseError, setBulkParseError] = useState<string | null>(null)
  const [increaseSeatCount, setIncreaseSeatCount] = useState(true)
  const createBulkUpload = useCreateBulkUpload({ orgId: orgId || '' })

  // Invite link state
  const inviteDurationOptions = [
    { label: '7 Days', hours: 7 * 24 },
    { label: '1 Month', hours: 30 * 24 },
    { label: '6 Months', hours: 180 * 24 },
    { label: '1 Year', hours: 365 * 24 },
  ]
  const [selectedDuration, setSelectedDuration] = useState(
    inviteDurationOptions[0]
  )
  const [isCustom, setIsCustom] = useState(false)
  const [customDateTime, setCustomDateTime] = useState('')
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null)
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false)

  const createUserMutation = useCreateUser()
  const { data: teams = [], isLoading: isLoadingTeams } = useFetchTeams(
    orgId || undefined
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const orgIdParam = urlParams.get('orgId')
    const orgNameParam = urlParams.get('orgName')

    if (orgIdParam) {
      setOrgId(orgIdParam)
      setOrgIdFromUrl(true)
    }
    if (orgNameParam) {
      setOrgName(decodeURIComponent(orgNameParam))
    }
  }, [location.search])

  // Auto-select team if only one exists
  useEffect(() => {
    if (teams.length === 1) {
      setSelectedTeamId(teams[0].id.toString())
    }
  }, [teams])

  const handleSelectOrg = (org: Organization) => {
    setOrgId(org.id)
    setOrgName(org.name)
    setOrgQuery('')
  }

  const handleClearOrg = () => {
    setOrgId(null)
    setOrgName(null)
    setSelectedTeamId(null)
    setCsvFileName(null)
    setBulkValid([])
    setBulkInvalid([])
    setBulkParseError(null)
  }

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCsvFileName(file.name)
    setBulkParseError(null)
    setBulkValid([])
    setBulkInvalid([])

    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const text = String(ev.target?.result || '')
        const rows = parseCSV(text)
        if (rows.length < 2) {
          setBulkParseError('CSV has no data rows')
          return
        }
        const headers = rows[0].map((h) => h.trim().toLowerCase())
        const backendKeys = headers.map((h) => CSV_HEADER_MAP[h] || null)
        const valid: BulkRow[] = []
        const invalid: BulkRow[] = []
        for (let r = 1; r < rows.length; r++) {
          const raw = rows[r]
          const obj = emptyBulkRow()
          backendKeys.forEach((k, idx) => {
            if (k) (obj as any)[k] = (raw[idx] || '').trim()
          })
          const errors = validateBulkRow(obj)
          if (errors.length) {
            obj.system_message = `Failed pre-processing: ${errors.join('; ')}`
            invalid.push(obj)
          } else {
            obj.system_message = 'Pre-processed successfully.'
            valid.push(obj)
          }
        }
        setBulkValid(valid)
        setBulkInvalid(invalid)
      } catch (err) {
        console.error(err)
        setBulkParseError('Failed to parse CSV')
      }
    }
    reader.readAsText(file)
  }

  const handleBulkUpload = () => {
    if (!orgId) return
    if (!bulkValid.length && !bulkInvalid.length) return
    createBulkUpload.mutate(
      {
        uploadData: {
          valid_data: bulkValid,
          invalid_data: bulkInvalid,
          increase_seat_count: increaseSeatCount,
        },
      },
      {
        onSuccess: () => {
          setCsvFileName(null)
          setBulkValid([])
          setBulkInvalid([])
          history.push(`/organizations/${orgId}/bulk-uploads`)
        },
      }
    )
  }

  const handleGenerateInvite = async () => {
    setIsGeneratingInvite(true)
    setInviteUrl(null)
    setInviteExpiresAt(null)
    try {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v1/signup_invites`,
        {
          method: API_METHODS.POST,
          headers: apiHeaders(store.getState()),
          body: JSON.stringify({
            expires_in_hours: isCustom
              ? (new Date(customDateTime).getTime() - Date.now()) / 3600000
              : selectedDuration.hours,
          }),
        }
      )
      if (!response.ok) {
        throw new Error('Failed to generate invite link')
      }
      const json = await response.json()
      setInviteUrl(json.invite_url)
      setInviteExpiresAt(json.expires_at)
      await navigator.clipboard.writeText(json.invite_url)
      toast('Invite link generated and copied to clipboard!', { icon: '🔗' })
    } catch (error) {
      console.error('Error generating invite link:', error)
      toast('Failed to generate invite link.')
    } finally {
      setIsGeneratingInvite(false)
    }
  }

  const handleCopyInviteUrl = () => {
    if (inviteUrl) {
      navigator.clipboard.writeText(inviteUrl)
      toast('Copied to clipboard!', { icon: '📋' })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Ensure at least one field is filled
    if (!name && !email && !phone) {
      return
    }

    createUserMutation.mutate(
      {
        name,
        email,
        phone,
        organizationId: orgId || undefined,
        teamId: selectedTeamId || undefined,
        increaseSeatCount: orgId ? increaseSeatCount : undefined,
      },
      {
        onSuccess: (user) => {
          // Redirect to the user details page with the new user
          history.push(`/user/${user.id}`)
        },
      }
    )
  }

  const showOrgPicker = !orgId && !orgIdFromUrl

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/users">Users</Breadcrumb.Item>
        <Breadcrumb.Item active>Create User</Breadcrumb.Item>
      </Breadcrumb>

      <h3 className="mt-4 mb-4">Create New User</h3>

      {orgId && orgName && (
        <Alert variant="warning" className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Adding user to organization:</strong> {orgName} (ID:{' '}
              {orgId})
            </div>
            {!orgIdFromUrl && (
              <Button size="sm" variant="outline-dark" onClick={handleClearOrg}>
                Change Organization
              </Button>
            )}
          </div>
        </Alert>
      )}

      {showOrgPicker && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Select Organization</Card.Title>
            <Card.Text className="text-muted">
              Search for an organization to add the user(s) to. Required for
              bulk upload.
            </Card.Text>
            <InputGroup className="mb-3">
              <FormControlOrg value={orgQuery} onChange={setOrgQuery} />
            </InputGroup>
            {debouncedOrgQuery && (
              <Table striped bordered hover responsive variant="dark">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isSearchingOrgs && (
                    <tr>
                      <td colSpan={3}>Searching...</td>
                    </tr>
                  )}
                  {!isSearchingOrgs && orgResults.length === 0 && (
                    <tr>
                      <td colSpan={3}>No organizations found</td>
                    </tr>
                  )}
                  {orgResults.map((org) => (
                    <tr key={org.id}>
                      <td>{org.id}</td>
                      <td>{org.name}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleSelectOrg(org)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      <p className="text-muted">
        At least one field (name, email, or phone) is required.
      </p>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter user's name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            placeholder="Enter phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Form.Group>

        {orgId && (
          <Form.Group className="mb-3">
            <Form.Label>Select Team</Form.Label>
            {isLoadingTeams ? (
              <div className="d-flex align-items-center">
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                <span className="text-muted">Loading teams...</span>
              </div>
            ) : teams.length > 0 ? (
              <>
                <select
                  value={selectedTeamId || ''}
                  onChange={(e) => setSelectedTeamId(e.target.value || null)}
                  className="form-select"
                >
                  <option value="">-- No Team --</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name} ({team.users_count} members)
                    </option>
                  ))}
                </select>
                <Form.Text className="text-muted d-block mt-2">
                  {teams.length === 1
                    ? 'This is the only team in the organization.'
                    : `Choose which team to add the user to.`}
                </Form.Text>
              </>
            ) : (
              <Form.Text className="text-muted d-block">
                No teams available in this organization.
              </Form.Text>
            )}
          </Form.Group>
        )}

        {orgId && (
          <Form.Group className="mb-3">
            <div className="d-flex flex-row align-items-center">
              <Form.Label className="mb-0 me-2">
                Automatically Increase Seat Count:
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle variant="info" className="p-1">
                  {increaseSeatCount ? 'Yes' : 'No'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setIncreaseSeatCount(true)}>
                    Yes
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setIncreaseSeatCount(false)}>
                    No
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Form.Group>
        )}

        <div className="d-flex gap-2">
          <Button
            variant="primary"
            type="submit"
            disabled={
              createUserMutation.isLoading || (!name && !email && !phone)
            }
          >
            {createUserMutation.isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Creating...
              </>
            ) : (
              'Create User'
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => history.goBack()}
            disabled={createUserMutation.isLoading}
          >
            Cancel
          </Button>
        </div>
      </Form>

      <hr className="my-5" />

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Bulk Upload Users</Card.Title>
          <Card.Text className="text-muted">
            Upload a CSV to create multiple users in the selected organization.
            Download the template, fill it in, and upload below.
          </Card.Text>

          {!orgId && (
            <Alert variant="info">
              Select an organization above to enable bulk upload.
            </Alert>
          )}

          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Button variant="outline-light" href="/bulk_upload_template.csv">
              Download CSV Template
            </Button>
            <div className="d-flex flex-row align-items-center">
              <span className="me-2">Automatically Increase Seat Count:</span>
              <Dropdown>
                <Dropdown.Toggle variant="info" className="p-1">
                  {increaseSeatCount ? 'Yes' : 'No'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setIncreaseSeatCount(true)}>
                    Yes
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setIncreaseSeatCount(false)}>
                    No
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>CSV File</Form.Label>
            <Form.Control
              type="file"
              accept=".csv,text/csv"
              disabled={!orgId}
              onChange={handleCsvFileChange}
            />
          </Form.Group>

          {bulkParseError && <Alert variant="danger">{bulkParseError}</Alert>}

          {csvFileName && !bulkParseError && (
            <Alert variant="secondary">
              <div>
                <strong>File:</strong> {csvFileName}
              </div>
              <div>
                <strong>Valid rows:</strong> {bulkValid.length} &nbsp;|&nbsp;
                <strong>Invalid rows:</strong> {bulkInvalid.length}
              </div>
            </Alert>
          )}

          {bulkInvalid.length > 0 && (
            <Table
              striped
              bordered
              hover
              responsive
              variant="dark"
              size="sm"
              className="mb-3"
            >
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Errors</th>
                </tr>
              </thead>
              <tbody>
                {bulkInvalid.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{r.email}</td>
                    <td>{`${r.first_name} ${r.last_name}`.trim()}</td>
                    <td>{r.system_message}</td>
                  </tr>
                ))}
                {bulkInvalid.length > 10 && (
                  <tr>
                    <td colSpan={4}>
                      ...and {bulkInvalid.length - 10} more invalid rows
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}

          <Button
            variant="primary"
            disabled={
              !orgId ||
              createBulkUpload.isLoading ||
              (bulkValid.length === 0 && bulkInvalid.length === 0)
            }
            onClick={handleBulkUpload}
          >
            {createBulkUpload.isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Uploading...
              </>
            ) : (
              `Upload ${bulkValid.length} Valid Row${
                bulkValid.length === 1 ? '' : 's'
              }`
            )}
          </Button>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Generate Signup Invite Link</Card.Title>
          <Card.Text className="text-muted">
            Generate a shareable link that lets users sign up directly. The link
            can be used by multiple people until it expires.
          </Card.Text>

          <Form.Group className="mb-3">
            <Form.Label>Link Expiration</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              {inviteDurationOptions.map((opt) => (
                <Button
                  key={opt.label}
                  variant={
                    !isCustom && selectedDuration.label === opt.label
                      ? 'primary'
                      : 'outline-light'
                  }
                  size="sm"
                  onClick={() => {
                    setIsCustom(false)
                    setSelectedDuration(opt)
                  }}
                >
                  {opt.label}
                </Button>
              ))}
              <Button
                variant={isCustom ? 'primary' : 'outline-light'}
                size="sm"
                onClick={() => setIsCustom(true)}
              >
                Custom
              </Button>
            </div>
          </Form.Group>

          {isCustom && (
            <Form.Group className="mb-3" style={{ maxWidth: 280 }}>
              <Form.Label>Expires on</Form.Label>
              <Form.Control
                type="datetime-local"
                value={customDateTime}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => setCustomDateTime(e.target.value)}
              />
            </Form.Group>
          )}

          <Button
            variant="outline-primary"
            onClick={handleGenerateInvite}
            disabled={isGeneratingInvite || (isCustom && !customDateTime)}
            className="mb-3"
          >
            {isGeneratingInvite ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Generating...
              </>
            ) : (
              `Generate Invite Link (${
                isCustom ? 'Custom' : selectedDuration.label
              })`
            )}
          </Button>

          {inviteUrl && (
            <>
              <InputGroup>
                <Form.Control
                  readOnly
                  value={inviteUrl}
                  onClick={(e: React.MouseEvent<HTMLInputElement>) =>
                    e.currentTarget.select()
                  }
                />
                <Button variant="outline-light" onClick={handleCopyInviteUrl}>
                  Copy
                </Button>
              </InputGroup>
              {inviteExpiresAt && (
                <Form.Text className="text-muted d-block mt-2">
                  Expires: {new Date(inviteExpiresAt).toLocaleString()}
                </Form.Text>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

const FormControlOrg = ({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) => (
  <Form.Control
    autoComplete="off"
    type="text"
    placeholder="Search organizations"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
)

export default UserCreatePage
